import isNil from "lodash/isNil";

const updateProfileCallback = (updatePayload: UpdateProfileRequest) => {
  // eslint-disable-next-line no-restricted-syntax -- this component has many props as it is a pass-through to the very complex updateProfile endpoint
  const {
    addressLine1,
    addressLine2,
    affiliationName,
    cellPhone,
    chosenName,
    city,
    contactPreferences,
    country,
    dateOfBirth,
    gender,
    languages,
    latitude,
    longitude,
    pronouns,
    state,
    timezone,
    zip,
  } = updatePayload;

  return (cacheData: FullProfileResponse) => ({
    ...cacheData,
    address: {
      ...cacheData.address,
      addressLine1,
      addressLine2,
      affiliationName,
      city,
      country,
      latitude,
      longitude,
      state,
      zip,
    },
    cellPhone,
    chosenName,
    contactPreferences,
    dateOfBirth: dateOfBirth ?? cacheData.dateOfBirth,
    gender: gender ?? cacheData.gender,
    languages,
    pronouns: pronouns ?? cacheData.pronouns,
    timezone: timezone ?? cacheData.timezone,
  });
};

const updateLinkedPatientProfileCallback = (
  updatePayload: UpdateLinkedPatientProfileRequest,
) => {
  const {
    // eslint-disable-next-line no-restricted-syntax -- this component has many props as it is a pass-through to the very complex updateProfile endpoint
    body: {
      addressLine1,
      addressLine2,
      affiliationName,
      cellPhone,
      chosenName,
      city,
      contactPreferences,
      country,
      dateOfBirth,
      gender,
      languages,
      latitude,
      longitude,
      pronouns,
      state,
      timezone,
      zip,
    },
    patientExternalId,
  } = updatePayload;

  // eslint-disable-next-line sonarjs/no-invariant-returns -- currently reported in SonarQube as unresolved
  return (cacheData: FullProfileResponse) => {
    const linkedPatient = cacheData.linkedPatients?.filter(
      (patient) => patient.externalId === patientExternalId,
    )[0];

    if (isNil(linkedPatient)) {
      return cacheData;
    }

    const updatedLinkedPatient = {
      ...linkedPatient,
      address: {
        ...linkedPatient.address,
        addressLine1,
        addressLine2,
        affiliationName,
        city,
        country,
        latitude,
        longitude,
        state,
        zip,
      },
      cellPhone,
      chosenName,
      contactPreferences,
      dateOfBirth: dateOfBirth ?? linkedPatient.dateOfBirth,
      gender: gender ?? linkedPatient.gender,
      languages,
      pronouns: pronouns ?? linkedPatient.pronouns,
      timezone: timezone ?? linkedPatient.timezone,
    };
    Object.assign(linkedPatient, updatedLinkedPatient);
    return cacheData;
  };
};

const updateProfileNotificationsCallback = (
  updatePayload: UpdateNotificationSettingsBody,
) => {
  const {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    receiveNotificationEmails: receiveEmails,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    receiveNotificationPush: receivePush,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    receiveNotificationSms: receiveSms,
  } = updatePayload;

  return (cacheData: FullProfileResponse) => ({
    ...cacheData,
    preferences: {
      ...cacheData.preferences,
      notifications: {
        ...cacheData.preferences?.notifications,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        receiveEmails,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        receivePush,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        receiveSms,
      },
    },
  });
};

const transformSafetyPlanDetailsResponse = (
  response: SafetyPlanDetailsResponse,
): SafetyPlanDetails => {
  const {
    data: { response: surveyResponseDetails },
  } = response;

  const safetyPlanResponse: SafetyPlanResponse = JSON.parse(
    surveyResponseDetails.jsonResult,
  );
  const {
    sp_imminent_risk_plan: imminentRiskPlan,
    sp_internal_coping_skills: copingSkills,
    sp_local_ems: localEmsPhone,
    sp_names_phones_supports: supportPeople,
    sp_other_warning_signs: otherWarningSigns,
    sp_reason_to_live: reasonToLive,
    sp_risk_level: riskLevel,
    sp_warning_signs: warningSigns,
  } = safetyPlanResponse;

  return {
    safetyPlan: {
      copingSkills,
      imminentRiskPlan,
      localEmsPhone,
      otherWarningSigns,
      reasonToLive,
      riskLevel,
      supportPeople,
      warningSigns,
    },
    surveyResponseDetails,
  };
};

const toContentExternalResource = (
  resource: ContentResource,
): ExternalResource => ({
  imageUrl: resource.imageUrl,
  link: resource.url,
  title: resource.title,
  type: resource.type,
});

const transformContentFeedResponse = (
  response: ContentFeedResponse,
): ResourceTheme[] => {
  return response.themes.map((theme) => {
    const { code, name, resources: rawResources } = theme;
    const transformedResources = rawResources.map(toContentExternalResource);
    return { code, name, resources: transformedResources };
  });
};

export {
  toContentExternalResource,
  transformContentFeedResponse,
  transformSafetyPlanDetailsResponse,
  updateLinkedPatientProfileCallback,
  updateProfileCallback,
  updateProfileNotificationsCallback,
};
